import { create } from "zustand";
import { nanoid } from "nanoid";
import { isValidEmail } from "../utils/emailValidator";
import { PhoneValidator } from "../utils/phoneValidator";
import { capitalizeFirstLetter } from "../utils/capitalizeLetter";
import { validateCanadaPostalCode } from "../utils/validatePostalCode";

interface CoApplicantData {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  postalCode: string;
  province: string;
  email: string;
  phone: string;
  dateOfBirth: Date | null;
  maritalStatus: string;
  relation: string;
  primaryIncomeSource: string;
  // totalMonthlyDebt: string;
  isFirstTimeHomeBuyer: boolean | null;
  annualBasePay: string;
  consent: boolean;
  errors: { [key: string]: string };
}

interface CoApplicantStore {
  coApplicants: CoApplicantData[];
  addCoApplicant: (id?: string) => void;
  removeCoApplicant: (id: string) => void;
  validateConsent: () => boolean;
  updateCoApplicantField: (
    id: string,
    field: keyof CoApplicantData,
    value: any
  ) => void;
  validateCoApplicants: () => boolean;
}

const useCoApplicantStore = create<
  CoApplicantStore & { clearCoApplicants: () => void }
>()((set, get) => ({
  coApplicants: [],

  addCoApplicant: (id?: string) =>
    set((state) => ({
      coApplicants: [
        ...state.coApplicants,
        {
          id: id || nanoid(),
          firstName: "",
          middleName: "",
          lastName: "",
          address: "",
          address2: "",
          city: "",
          postalCode: "",
          province: "",
          email: "",
          phone: "",
          dateOfBirth: null,
          annualBasePay: "",
          maritalStatus: "",
          relation: "",
          primaryIncomeSource: "",
          // totalMonthlyDebt: "",
          consent: false,
          isFirstTimeHomeBuyer: null,
          errors: {},
        },
      ],
    })),

  removeCoApplicant: (id) =>
    set((state) => ({
      coApplicants: state.coApplicants.filter(
        (applicant) => applicant.id !== id
      ),
    })),

  clearCoApplicants: () =>
    set(() => ({
      coApplicants: [],
    })),

  updateCoApplicantField: (id, field, value) =>
    set((state) => ({
      coApplicants: state.coApplicants.map((applicant) =>
        applicant.id === id
          ? {
              ...applicant,
              [field]:
                field === "firstName" ||
                field === "middleName" ||
                field === "lastName"
                  ? capitalizeFirstLetter(value)
                  : field === "postalCode"
                  ? value.toUpperCase()
                  : value,
              errors: {
                ...applicant.errors,
                [field]:
                  value === null || value === ""
                    ? "This field is required"
                    : "",
              },
            }
          : applicant
      ),
    })),

  validateConsent: () => {
    const { coApplicants } = get();
    let allConsentsValid = true;

    const updatedCoApplicants = coApplicants.map((applicant) => {
      if (!applicant.consent) {
        allConsentsValid = false;
        return {
          ...applicant,
          errors: {
            ...applicant.errors,
            consent: "Consent is required",
          },
        };
      }
      return {
        ...applicant,
        errors: {
          ...applicant.errors,
          consent: "", // Clear consent error if valid
        },
      };
    });

    set({ coApplicants: updatedCoApplicants });

    return allConsentsValid;
  },

  validateCoApplicants: () => {
    let isValid = true;

    set((state) => ({
      coApplicants: state.coApplicants.map((applicant) => {
        const errors = { ...applicant.errors };

        const requiredFields = [
          "firstName",
          "lastName",
          "address",
          "city",
          "postalCode",
          "province",
          "phone",
          "dateOfBirth",
          "maritalStatus",
          "relation",
          "primaryIncomeSource",
          "email",
          "annualBasePay",
          // "totalMonthlyDebt",
        ];

        for (const field of requiredFields) {
          if (
            applicant[field as keyof CoApplicantData] === null ||
            applicant[field as keyof CoApplicantData] === ""
          ) {
            errors[field] = "This field is required";
            isValid = false;
          }
        }

        if (applicant.isFirstTimeHomeBuyer === null) {
          errors.isFirstTimeHomeBuyer = "This field is required";
          isValid = false;
        } else {
          errors.isFirstTimeHomeBuyer = "";
        }

        if (applicant.phone) {
          try {
            const isValidPhone =
              PhoneValidator.isValid(applicant.phone, "CA") ||
              PhoneValidator.isValid(applicant.phone, "US");

            if (!isValidPhone) {
              errors.phone = "Invalid phone number format for CA or US";
              isValid = false;
            }
          } catch (error) {
            errors.phone = "Invalid phone number format";
            isValid = false;
          }
        }

        if (applicant.email && !isValidEmail(applicant.email)) {
          errors.email = "Invalid email format";
          isValid = false;
        }

        if (applicant.postalCode) {
          if (!validateCanadaPostalCode(applicant.postalCode)) {
            errors.postalCode = "Invalid Canadian postal code format";
            isValid = false;
          } else {
            errors.postalCode = "";
          }
        }
        return { ...applicant, errors };
      }),
    }));

    return isValid;
  },
}));

export { useCoApplicantStore };
